import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Grid,
  Card,
  CardContent,
  IconButton,
  Avatar,
  Divider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  AppBar,
  Toolbar,
  Badge
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  Payment as PaymentIcon,
  LocationOn as LocationIcon,
  AddLocation as AddLocationIcon,
  Home as HomeIcon,
  Work as WorkIcon,
  CheckCircle as CheckCircleIcon,
  Person as PersonIcon,
  AccountCircle as AccountCircleIcon
} from '@mui/icons-material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import customerAuthService from '../../services/customerAuthService';
import ecommerceOrderService from '../../services/ecommerceOrderService';
import useCustomerSessionTimeout from '../../hooks/useCustomerSessionTimeout';
import { formatCurrency } from '../../utils/currencyFormatter';
import API_CONFIG from '../../config/api.config';

const steps = ['Review Cart', 'Payment & Review'];

const CartCheckoutPage = () => {
  const { websitePath } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const cartStorageKey = useMemo(() => `ecommerce_cart_${websitePath}`, [websitePath]);

  const [activeStep, setActiveStep] = useState(0);
  const [customer, setCustomer] = useState(null);
  const [cart, setCart] = useState([]);
  /** Avoid writing [] to localStorage before first read (would wipe saved cart on mount). */
  const [cartHydrated, setCartHydrated] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('CASH'); // Default to CASH
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(null); // { orderNumber, total }
  const [websiteData, setWebsiteData] = useState(null);
  const [newAddress, setNewAddress] = useState({
    type: 'home',
    label: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    pincode: '',
    phone: '',
    isDefault: false
  });

  // Session timeout tracking
  useCustomerSessionTimeout();

  useEffect(() => {
    const currentCustomer = customerAuthService.getCurrentCustomer();
    if (!currentCustomer) {
      navigate(`/${websitePath}`);
      return;
    }
    setCustomer(currentCustomer);
    
    // Load addresses from localStorage
    const savedAddresses = localStorage.getItem(`customer_addresses_${currentCustomer.id}`);
    if (savedAddresses) {
      const parsed = JSON.parse(savedAddresses);
      setAddresses(parsed);
      // Set default address if available
      const defaultAddr = parsed.find(addr => addr.isDefault);
      if (defaultAddr) {
        setSelectedAddress(defaultAddr);
      } else if (parsed.length > 0) {
        setSelectedAddress(parsed[0]);
      }
    }

    // Fetch website data for topbar
    const fetchWebsiteData = async () => {
      try {
        const response = await fetch(`${API_CONFIG.BASE_URL}/public/website/${websitePath}`);
        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            setWebsiteData(data);
          }
        }
      } catch (err) {
        console.error('Error fetching website data:', err);
      }
    };
    if (websitePath) fetchWebsiteData();
  }, [websitePath, navigate]);

  // Replace state from localStorage (source of truth). Empty cart in storage must clear UI.
  const loadCartFromStorage = useCallback(() => {
    try {
      const raw = localStorage.getItem(cartStorageKey);
      if (!raw) {
        setCart([]);
        return;
      }
      const parsed = JSON.parse(raw);
      setCart(Array.isArray(parsed) ? parsed : []);
    } catch (e) {
      console.error('[CartCheckoutPage] Failed to load cart from storage', e);
    }
  }, [cartStorageKey]);

  // Initial hydrate: read storage once per website path, then allow persistence writes.
  useEffect(() => {
    loadCartFromStorage();
    setCartHydrated(true);
    return () => setCartHydrated(false);
  }, [cartStorageKey, loadCartFromStorage]);

  // When route changes, re-sync from storage (e.g. returned from storefront).
  useEffect(() => {
    if (!cartHydrated) return;
    loadCartFromStorage();
  }, [location.pathname, cartHydrated, loadCartFromStorage]);

  useEffect(() => {
    if (!cartHydrated) return;

    const handleFocus = () => loadCartFromStorage();
    const handleStorageChange = (e) => {
      if (e.key === cartStorageKey) {
        loadCartFromStorage();
      }
    };

    const pollInterval = setInterval(loadCartFromStorage, 3000);

    window.addEventListener('focus', handleFocus);
    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(pollInterval);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [cartStorageKey, cartHydrated, loadCartFromStorage]);

  useEffect(() => {
    if (!cartHydrated) return;
    try {
      localStorage.setItem(cartStorageKey, JSON.stringify(cart));
      if (cart.length > 0) {
        console.log('[CartCheckoutPage] Saved cart to storage:', cart.length, 'items');
      }
    } catch (e) {
      console.error('[CartCheckoutPage] Failed to save cart to storage', e);
    }
  }, [cart, cartStorageKey, cartHydrated]);

  const updateQuantity = (productId, delta) => {
    setCart(prev =>
      prev
        .map(item =>
          item.productId === productId
            ? { ...item, quantity: Math.max(1, item.quantity + delta) }
            : item
        )
        .filter(item => item.quantity > 0)
    );
  };

  const removeItem = (productId) => {
    setCart(prev => prev.filter(item => item.productId !== productId));
  };

  const handleNext = () => {
    if (activeStep === 0 && cart.length === 0) {
      return; // Can't proceed with empty cart
    }
    // Removed address requirement - can proceed without address for now
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleAddMore = () => {
    navigate(`/${websitePath}`);
  };

  const handleSaveAddress = () => {
    const addressWithId = { ...newAddress, id: Date.now() };
    const newAddresses = [...addresses, addressWithId];
    if (newAddress.isDefault) {
      newAddresses.forEach(addr => {
        if (addr.id !== addressWithId.id) addr.isDefault = false;
      });
    }
    setAddresses(newAddresses);
    if (customer) {
      localStorage.setItem(`customer_addresses_${customer.id}`, JSON.stringify(newAddresses));
    }
    setSelectedAddress(addressWithId);
    setAddressDialogOpen(false);
    setNewAddress({
      type: 'home',
      label: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      pincode: '',
      phone: '',
      isDefault: false
    });
  };

  const handlePlaceOrder = async () => {
    if (!customer || cart.length === 0) return;

    try {
      setPlacingOrder(true);

      // For now, always use CASH as payment method
      // Address will be handled later when we have address storage
      const getLineWithTax = (item) => {
        const base = Number(item?.unitPrice || 0);
        const explicit = Number(item?.priceWithTax);
        if (Number.isFinite(explicit) && explicit > 0) return explicit * Number(item?.quantity || 0);
        const rate = Number(item?.taxRate || 0);
        return (base * (1 + (rate / 100))) * Number(item?.quantity || 0);
      };
      const cartTotal = cart.reduce((sum, item) => sum + getLineWithTax(item), 0);
      
      const orderData = {
        paymentMethod: 'CASH', // Always CASH for now
        paymentStatus: 'PAID', // Set payment status as PAID for cash orders
        items: cart.map(item => ({
          productId: item.productId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          discount: 0,
          taxId: item.taxId ?? null
        }))
        // shippingAddress will be added later when address storage is implemented
      };

      const response = await ecommerceOrderService.placeOrder(websitePath, orderData);

      if (response.success) {
        // Show success dialog
        setOrderSuccess({
          orderNumber: response.orderNumber || response.order?.orderNumber,
          total: cartTotal
        });
        
        // Clear cart and storage
        setCart([]);
        localStorage.removeItem(cartStorageKey);
        
        // Navigate to orders page after 2 seconds
        setTimeout(() => {
          navigate(`/${websitePath}/orders`, { state: { justOrdered: true, orderNumber: response.orderNumber || response.order?.orderNumber } });
        }, 2000);
      } else {
        throw new Error(response.message || 'Failed to place order');
      }
    } catch (err) {
      console.error('Error placing order:', err);
      alert(err.message || 'Failed to place order. Please try again.');
    } finally {
      setPlacingOrder(false);
    }
  };

  const mapPaymentMethodForBackend = (method) => {
    switch (method) {
      case 'CASH': return 'CASH';
      case 'CARD': return 'CREDIT_CARD';
      case 'UPI': return 'UPI';
      case 'CREDIT': return 'CREDIT';
      default: return 'CREDIT';
    }
  };

  const getProductImageUrl = (imageUrl) => {
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    const baseUrl = API_CONFIG.BASE_URL.replace('/api', '');
    return imageUrl.startsWith('/api') ? `${baseUrl}${imageUrl}` : `${baseUrl}/api${imageUrl}`;
  };

  const getLogoUrl = (logoUrl) => {
    if (!logoUrl) return '';
    if (logoUrl.startsWith('/api')) {
      const baseUrl = API_CONFIG.BASE_URL.replace('/api', '');
      return `${baseUrl}${logoUrl}`;
    }
    return logoUrl.startsWith('http') ? logoUrl : `${API_CONFIG.BASE_URL.replace('/api', '')}/api/${logoUrl}`;
  };

  

  const getLineWithTax = (item) => {
    const base = Number(item?.unitPrice || 0);
    const explicit = Number(item?.priceWithTax);
    if (Number.isFinite(explicit) && explicit > 0) return explicit * Number(item?.quantity || 0);
    const rate = Number(item?.taxRate || 0);
    return (base * (1 + (rate / 100))) * Number(item?.quantity || 0);
  };
  const getUnitWithTax = (item) => {
    const explicit = Number(item?.priceWithTax);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    const base = Number(item?.unitPrice || 0);
    const rate = Number(item?.taxRate || 0);
    return base * (1 + (rate / 100));
  };
  const cartTotal = cart.reduce((sum, item) => sum + getLineWithTax(item), 0);
  const shippingCost = 0; // Can be calculated later
  const discount = 0; // Can be added later
  const tax = 0; // Can be calculated later
  const netTotal = cartTotal + shippingCost - discount + tax;

  const theme = createTheme({
    palette: {
      primary: { main: '#1976d2' },
      secondary: { main: '#dc004e' }
    }
  });

  if (!customer) {
    return (
      <ThemeProvider theme={theme}>
        <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
        {/* Topbar */}
        <AppBar 
          position="sticky" 
          sx={{ 
            bgcolor: 'white',
            color: 'text.primary',
            boxShadow: 2
          }}
        >
          <Container maxWidth="lg">
            <Toolbar sx={{ py: 1.5, justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  onClick={() => navigate(`/${websitePath}`)}
                  sx={{ color: 'text.primary' }}
                >
                  <ArrowBackIcon />
                </IconButton>
                {websiteData?.logoUrl && (
                  <Avatar
                    src={getLogoUrl(websiteData.logoUrl)}
                    sx={{ width: 40, height: 40, borderRadius: 1 }}
                    variant="rounded"
                  />
                )}
                <Typography variant="h6" fontWeight={700} sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {websiteData?.companyName || 'Checkout'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {customer && (
                  <IconButton
                    onClick={() => navigate(`/${websitePath}/profile`)}
                    sx={{ color: 'text.primary' }}
                  >
                    <AccountCircleIcon />
                  </IconButton>
                )}
                <IconButton
                  onClick={() => navigate(`/${websitePath}/cart`)}
                  sx={{ color: 'text.primary' }}
                >
                  <Badge badgeContent={cart.length} color="primary">
                    <ShoppingCartIcon />
                  </Badge>
                </IconButton>
              </Box>
            </Toolbar>
          </Container>
        </AppBar>

        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 }, flexGrow: 1 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight={700}>
              Checkout
            </Typography>
          </Box>

          {/* Stepper */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Stepper activeStep={activeStep} alternativeLabel>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>

          <Grid container spacing={3}>
            {/* Main Content */}
            <Grid item xs={12} md={8}>
              {activeStep === 0 && (
                <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h6" fontWeight={700}>
                      Cart Items ({cart.length})
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={handleAddMore}
                      sx={{ borderRadius: 2 }}
                    >
                      Add More Products
                    </Button>
                  </Box>

                  {cart.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <ShoppingCartIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        Your cart is empty
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={handleAddMore}
                        sx={{ mt: 2, borderRadius: 2, bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                      >
                        Continue Shopping
                      </Button>
                    </Box>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {cart.map((item) => (
                        <Card key={item.productId} sx={{ borderRadius: 2, boxShadow: 1, mb: 2 }}>
                          <CardContent sx={{ display: 'flex', gap: 2, p: 2 }}>
                            <Avatar
                              variant="rounded"
                              src={getProductImageUrl(item.productImage)}
                              sx={{ width: 80, height: 80, bgcolor: 'grey.100' }}
                            >
                              {item.productName?.charAt(0)}
                            </Avatar>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                {item.productName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" gutterBottom>
                                {formatCurrency(getUnitWithTax(item))} each
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => updateQuantity(item.productId, -1)}
                                  sx={{ border: 1, borderColor: 'divider' }}
                                >
                                  <RemoveIcon fontSize="small" />
                                </IconButton>
                                <Typography variant="body1" sx={{ minWidth: 40, textAlign: 'center' }}>
                                  {item.quantity}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => updateQuantity(item.productId, 1)}
                                  sx={{ border: 1, borderColor: 'divider' }}
                                >
                                  <AddIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => removeItem(item.productId)}
                                  sx={{ ml: 'auto' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            </Box>
                            <Box sx={{ minWidth: 100, textAlign: 'right' }}>
                              <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                                {formatCurrency(getLineWithTax(item))}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}
                    </Box>
                  )}
                </Paper>
              )}

              {activeStep === 1 && (
                <>
                  {/* Payment Method */}
                  <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PaymentIcon sx={{ mr: 1 }} />
                      <Typography variant="h6" fontWeight={700}>
                        Payment Method
                      </Typography>
                    </Box>
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Payment method selection will be available soon. Currently, all orders are processed as Cash on Delivery.
                    </Alert>
                    <RadioGroup
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      disabled
                    >
                      <FormControlLabel value="CASH" control={<Radio />} label="Cash on Delivery (Default)" />
                      <FormControlLabel value="CARD" control={<Radio />} label="Credit/Debit Card (Coming Soon)" disabled />
                      <FormControlLabel value="UPI" control={<Radio />} label="UPI (Coming Soon)" disabled />
                      <FormControlLabel value="CREDIT" control={<Radio />} label="Credit (Coming Soon)" disabled />
                    </RadioGroup>
                  </Paper>

                  {/* Note about address */}
                  <Paper sx={{ p: 3, mb: 3, borderRadius: 2, boxShadow: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Note:</strong> Address selection will be available once we implement address storage. 
                      For now, orders will be processed with the contact information from your profile.
                    </Typography>
                  </Paper>
                </>
              )}
            </Grid>

            {/* Order Summary Sidebar */}
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: 2,
                  boxShadow: 2,
                  position: { md: 'sticky' },
                  top: { md: 20 }
                }}
              >
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Order Summary
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Items ({cart.length})
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatCurrency(cartTotal)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      Shipping
                    </Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {formatCurrency(shippingCost)}
                    </Typography>
                  </Box>
                  {discount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Discount
                      </Typography>
                      <Typography variant="body2" fontWeight={600} color="success.main">
                        -{formatCurrency(discount)}
                      </Typography>
                    </Box>
                  )}
                  {tax > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">
                        Tax
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(tax)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" fontWeight={700}>
                    Total
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    {formatCurrency(netTotal)}
                  </Typography>
                </Box>

                {activeStep === 0 && (
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleNext}
                    disabled={cart.length === 0}
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      bgcolor: 'primary.main',
                      '&:hover': { bgcolor: 'primary.dark' },
                      boxShadow: 3
                    }}
                  >
                    Continue to Payment
                  </Button>
                )}

                {activeStep === 1 && (
                  <>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="large"
                      onClick={handleBack}
                      sx={{ mb: 2, py: 1.5, borderRadius: 2 }}
                    >
                      Back to Cart
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      size="large"
                      onClick={handlePlaceOrder}
                      disabled={placingOrder || cart.length === 0}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        bgcolor: 'primary.main',
                        '&:hover': { bgcolor: 'primary.dark' },
                        boxShadow: 3
                      }}
                    >
                      {placingOrder ? (
                        <>
                          <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                          Placing Order...
                        </>
                      ) : (
                        'Place Order'
                      )}
                    </Button>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>

          {/* Add Address Dialog */}
          <Dialog
            open={addressDialogOpen}
            onClose={() => setAddressDialogOpen(false)}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
          >
            <DialogTitle>Add New Address</DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Address Type</InputLabel>
                  <Select
                    value={newAddress.type}
                    onChange={(e) => setNewAddress({ ...newAddress, type: e.target.value })}
                    label="Address Type"
                  >
                    <MenuItem value="home">Home</MenuItem>
                    <MenuItem value="work">Work</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Label (e.g., My Home)"
                  value={newAddress.label}
                  onChange={(e) => setNewAddress({ ...newAddress, label: e.target.value })}
                />
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={newAddress.addressLine1}
                  onChange={(e) => setNewAddress({ ...newAddress, addressLine1: e.target.value })}
                  required
                />
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={newAddress.addressLine2}
                  onChange={(e) => setNewAddress({ ...newAddress, addressLine2: e.target.value })}
                />
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="City"
                      value={newAddress.city}
                      onChange={(e) => setNewAddress({ ...newAddress, city: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="State"
                      value={newAddress.state}
                      onChange={(e) => setNewAddress({ ...newAddress, state: e.target.value })}
                      required
                    />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Pincode"
                      value={newAddress.pincode}
                      onChange={(e) => setNewAddress({ ...newAddress, pincode: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      label="Phone"
                      value={newAddress.phone}
                      onChange={(e) => setNewAddress({ ...newAddress, phone: e.target.value })}
                    />
                  </Grid>
                </Grid>
                <FormControlLabel
                  control={
                    <Radio
                      checked={newAddress.isDefault}
                      onChange={(e) => setNewAddress({ ...newAddress, isDefault: e.target.checked })}
                    />
                  }
                  label="Set as default address"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setAddressDialogOpen(false)}>Cancel</Button>
              <Button
                variant="contained"
                onClick={handleSaveAddress}
                disabled={!newAddress.addressLine1 || !newAddress.city || !newAddress.state || !newAddress.pincode}
              >
                Save Address
              </Button>
            </DialogActions>
          </Dialog>

          {/* Order Success Dialog */}
          <Dialog
            open={orderSuccess !== null}
            onClose={() => {}} // Prevent closing manually
            maxWidth="sm"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: 3,
                textAlign: 'center',
                p: 4
              }
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
            <DialogTitle sx={{ pb: 1 }}>
              <Typography variant="h5" fontWeight={700} color="success.main">
                Order Placed Successfully!
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Thank you for your order. We've received your order and will process it shortly.
              </Typography>
              {orderSuccess && (
                <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 2, mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Order Number
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color="primary.main">
                    {orderSuccess.orderNumber}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Total Amount: {formatCurrency(orderSuccess.total || 0)}
                  </Typography>
                </Box>
              )}
              <Typography variant="body2" color="text.secondary">
                Redirecting to orders page...
              </Typography>
            </DialogContent>
          </Dialog>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default CartCheckoutPage;

